import { useState } from 'react'
import {
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  UserRoundPlus,
  Users,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import {
  useCreateUser,
  useDeleteUser,
  useResetUserPassword,
  useUpdateUser,
  useUsers,
} from '@/features/users/hooks'
import { OneTimePasswordDialog } from '@/features/users/components/one-time-password-dialog'
import { useRoles } from '@/features/roles/hooks'
import { useMe } from '@/features/auth/hooks'
import type { User } from '@/types/auth'

interface UserFormState {
  username: string
  display_name: string
  email: string
  role_id: string
  active: boolean
  // "" = leave unchanged when editing; required when creating
  password: string
}

const emptyUserForm: UserFormState = {
  username: '',
  display_name: '',
  email: '',
  role_id: '',
  active: true,
  password: '',
}

export function UsersTab() {
  const { data: users, isLoading, isError } = useUsers()
  const { data: roles } = useRoles()
  const { data: me } = useMe()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<User | null>(null)
  const [form, setForm] = useState<UserFormState>(emptyUserForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<User | null>(null)
  const [resetting, setResetting] = useState<User | null>(null)
  const [resetResult, setResetResult] = useState<string | null>(null)

  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const deleteUser = useDeleteUser()
  const resetPassword = useResetUserPassword()
  const saving = createUser.isPending || updateUser.isPending

  const onResetPassword = () => {
    if (!resetting) return
    resetPassword.mutate(resetting.id, {
      onSuccess: (data) => {
        setResetResult(data.password)
        setResetting(null)
      },
    })
  }

  const openCreate = () => {
    setEditing(null)
    setForm({ ...emptyUserForm, role_id: roles?.find((r) => !r.is_system)?.id ?? '' })
    setFormError(null)
    setDialogOpen(true)
  }

  const openEdit = (user: User) => {
    setEditing(user)
    setForm({
      username: user.username,
      display_name: user.display_name,
      email: user.email ?? '',
      role_id: user.role_id,
      active: user.active,
      password: '',
    })
    setFormError(null)
    setDialogOpen(true)
  }

  const onSave = () => {
    setFormError(null)
    if (!form.role_id) {
      setFormError('Pick a role for the user.')
      return
    }
    const body = {
      display_name: form.display_name,
      email: form.email || undefined,
      role_id: form.role_id,
      active: form.active,
    }
    if (editing) {
      updateUser.mutate(
        {
          id: editing.id,
          body: { ...body, password: form.password === '' ? undefined : form.password },
        },
        {
          onSuccess: () => setDialogOpen(false),
          onError: (err) => setFormError(err.message),
        },
      )
    } else {
      createUser.mutate(
        { ...body, username: form.username, password: form.password },
        {
          onSuccess: () => setDialogOpen(false),
          onError: (err) => setFormError(err.message),
        },
      )
    }
  }

  const onDelete = () => {
    if (!deleting) return
    deleteUser.mutate(deleting.id, {
      onSuccess: () => setDeleting(null),
    })
  }

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Account users
            </CardTitle>
            <CardDescription>
              People who can sign in to this account, scoped by their role's flows.
            </CardDescription>
          </div>
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" /> New user
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <p className="text-destructive text-sm">Failed to load users.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(users ?? []).map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="font-medium">{user.display_name || user.username}</div>
                      <div className="text-muted-foreground flex items-center gap-1 text-xs">
                        @{user.username}
                        {!user.has_password && (
                          <span title="Signs in with a passkey only">
                            <KeyRound className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{user.email || '—'}</TableCell>
                    <TableCell>
                      <Badge variant={user.role_is_system ? 'default' : 'secondary'}>
                        {user.role_name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.active ? 'outline' : 'destructive'}>
                        {user.active ? 'active' : 'disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(user)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Reset password (generates a one-time password)"
                          disabled={resetPassword.isPending}
                          onClick={() => setResetting(user)}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={me?.kind === 'user' && user.id === me.id}
                          title={
                            me?.kind === 'user' && user.id === me.id
                              ? 'You cannot delete your own account'
                              : 'Delete user'
                          }
                          onClick={() => setDeleting(user)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit @${editing.username}` : 'Create user'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Leave the password blank to keep the current one. Set it to an empty-save to clear (passkey-only).'
                : 'Assign a role — the Admin role has full access; custom roles only see their flows.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-2">
              <Label htmlFor="u-username">Username</Label>
              <Input
                id="u-username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                disabled={!!editing}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="u-display">Display name</Label>
              <Input
                id="u-display"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="u-email">Email (optional)</Label>
              <Input
                id="u-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Role</Label>
                <Select
                  value={form.role_id}
                  onValueChange={(v) => setForm({ ...form, role_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {(roles ?? []).map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                        {role.is_system ? ' (full access)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="u-password">{editing ? 'New password' : 'Password'}</Label>
                <Input
                  id="u-password"
                  type="password"
                  value={form.password}
                  placeholder={editing ? 'unchanged' : 'min 8 characters'}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
            </div>
            {editing && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant={form.active ? 'outline' : 'default'}
                  size="sm"
                  onClick={() => setForm({ ...form, active: !form.active })}
                >
                  {form.active ? 'Deactivate user' : 'Activate user'}
                </Button>
                <p className="text-muted-foreground text-xs">
                  Deactivating signs the user out everywhere.
                </p>
              </div>
            )}
            {formError && <p className="text-destructive text-sm">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? 'Save changes' : 'Create user'}
              {!editing && <UserRoundPlus className="ml-2 h-4 w-4" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete @{deleting?.username}?</DialogTitle>
            <DialogDescription>
              This permanently removes the user and all their passkeys. Their sessions are revoked
              immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleting(null)}
              disabled={deleteUser.isPending}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={onDelete} disabled={deleteUser.isPending}>
              {deleteUser.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetting} onOpenChange={(open) => !open && setResetting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset @{resetting?.username}'s password?</DialogTitle>
            <DialogDescription>
              This generates a one-time temporary password and signs the user out everywhere. Any
              pending email reset links for them are invalidated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetting(null)}
              disabled={resetPassword.isPending}
            >
              Cancel
            </Button>
            <Button onClick={onResetPassword} disabled={resetPassword.isPending}>
              {resetPassword.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {resetResult && (
        <OneTimePasswordDialog
          password={resetResult}
          title="Temporary password"
          description="Share it with the user through a secure channel — it is shown only once and they should change it after signing in."
          onClose={() => setResetResult(null)}
        />
      )}
    </>
  )
}
